import { useState } from "react";

import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
  useTheme
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faListCheck } from "@fortawesome/pro-solid-svg-icons";

import { CategoriesAvatar } from "@aclymatepackages/atoms";
import findSubcategory from "@aclymatepackages/subcategories";

import Card from "../atoms/mui/Card";
import {
  useCachedDisplayData,
  useCachedFirebaseCrud
} from "../../helpers/firebase";
import usePlatformLayoutContext from "../../helpers/contexts/platformLayout";

const EmptyState = () => {
  const { setAgentChatOpen } = usePlatformLayoutContext();
  return (
    <Box px={4} pb={3} display="flex" alignItems="center" style={{ gap: 16 }}>
      <Typography variant="body2" color="textSecondary">
        No reduction actions saved yet.
      </Typography>
      <Button
        size="small"
        variant="outlined"
        color="primary"
        onClick={() => setAgentChatOpen(true)}
      >
        Ask the AI assistant
      </Button>
    </Box>
  );
};

const ActionItemRow = ({ item }) => {
  const { palette } = useTheme();
  const { updateCollectionDoc } = useCachedFirebaseCrud();
  const [isUpdating, setIsUpdating] = useState(false);

  const { id, title, category, status } = item;
  const isDone = status === "done";
  const subcategoryObj = category ? findSubcategory(category) : null;

  const toggleStatus = async () => {
    setIsUpdating(true);
    await updateCollectionDoc("action-items", id, {
      status: isDone ? "open" : "done"
    });
    setIsUpdating(false);
  };

  const handleDelete = () =>
    updateCollectionDoc("action-items", id, { archived: true });

  return (
    <ListItem alignItems="flex-start" disableGutters>
      <ListItemIcon style={{ minWidth: 36, marginTop: 4 }}>
        {isUpdating ? (
          <CircularProgress size={20} />
        ) : (
          <IconButton size="small" onClick={toggleStatus}>
            {isDone ? (
              <CheckBoxIcon style={{ color: palette.secondary.main }} />
            ) : (
              <CheckBoxOutlineBlankIcon color="action" />
            )}
          </IconButton>
        )}
      </ListItemIcon>
      {subcategoryObj && (
        <ListItemIcon style={{ minWidth: 36, marginTop: 8 }}>
          <CategoriesAvatar subcategory={category} size="small" />
        </ListItemIcon>
      )}
      <ListItemText
        primary={
          <Typography
            variant="body2"
            style={{
              textDecoration: isDone ? "line-through" : "none",
              color: isDone ? palette.text.disabled : "inherit"
            }}
          >
            {title}
          </Typography>
        }
      />
      <ListItemSecondaryAction>
        <IconButton size="small" onClick={handleDelete}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </ListItemSecondaryAction>
    </ListItem>
  );
};

const ActionItemsModule = ({ title, type }) => {
  const [actionItems, actionItemsLoading] =
    useCachedDisplayData("action-items");

  const filteredItems = type
    ? (actionItems || []).filter((item) =>
        item.view ? item.view === type : item.category === type
      )
    : actionItems || [];

  const moduleTitle = title
    ? `${title} Reduction Actions`
    : "Reduction Actions";

  return (
    <Card
      cardType="layout"
      icon={<FontAwesomeIcon icon={faListCheck} />}
      title={moduleTitle}
      subtitle="Actions recommended by your AI sustainability assistant. Check items off as you complete them."
      isLoading={actionItemsLoading}
      content={
        filteredItems.length === 0 ? (
          <EmptyState />
        ) : (
          <Box px={2} pb={2}>
            <List dense disablePadding>
              {filteredItems.map((item) => (
                <ActionItemRow key={item.id} item={item} />
              ))}
            </List>
          </Box>
        )
      }
    />
  );
};
export default ActionItemsModule;
